import { useState, useEffect } from 'react';

interface Step {
  step: string;
  detail: string;
}

interface Platform {
  id: string;
  label: string;
  icon: string;
  color: string;
  steps: Step[];
}

const PLATFORMS: Platform[] = [
  {
    id: 'meta',
    label: 'Meta Ads Manager',
    icon: 'ri-meta-line',
    color: 'text-cyan-400',
    steps: [
      { step: 'Open Ads Manager', detail: 'Go to business.facebook.com/adsmanager and sign into your Business account.' },
      { step: 'Create a Campaign', detail: 'Click "Create" and choose your campaign objective (e.g. Conversions, Traffic, Awareness).' },
      { step: 'Set Up Ad Set', detail: 'Define your audience, budget, schedule, and placements under the Ad Set level.' },
      { step: 'Create the Ad', detail: 'At the Ad level, choose your Facebook Page and Instagram account, then select "Single Image or Video".' },
      { step: 'Upload Your Creative', detail: 'Click "Add Media" → "Add Image" and upload your exported PNG or JPG file (ad-square.png or ad-landscape.png).' },
      { step: 'Add Copy & CTA', detail: 'Paste your headline, primary text, and CTA from ad-copy.txt. Set your destination URL.' },
      { step: 'Review & Publish', detail: 'Click "Publish" and your ad will enter Meta\'s review process (usually under 24 hours).' },
    ],
  },
  {
    id: 'instagram',
    label: 'Instagram Post Upload',
    icon: 'ri-instagram-line',
    color: 'text-pink-400',
    steps: [
      { step: 'Open Instagram App', detail: 'Use the Instagram mobile app or Creator Studio for scheduled posts.' },
      { step: 'Tap the + Button', detail: 'Tap the "+" icon at the bottom center of the app to create a new post.' },
      { step: 'Select Your Asset', detail: 'Choose your exported image from your gallery (ad-square.png for Feed, ad-story.png for Stories).' },
      { step: 'For Stories', detail: 'Swipe to "Story" mode and upload ad-story.png. Tap "Your Story" to post, or add interactive elements first.' },
      { step: 'Add Caption', detail: 'Paste your caption and hashtags from ad-copy.txt into the caption field.' },
      { step: 'Tag & Location', detail: 'Optionally add location, product tags, or collaborator tags to boost reach.' },
      { step: 'Share', detail: 'Tap "Share" to publish, or schedule using Instagram\'s native scheduling or Meta Business Suite.' },
    ],
  },
  {
    id: 'google',
    label: 'Google Ads Asset Upload',
    icon: 'ri-google-fill',
    color: 'text-green-400',
    steps: [
      { step: 'Open Google Ads', detail: 'Go to ads.google.com and navigate to your campaign or create a new Display campaign.' },
      { step: 'Select Display Campaign', detail: 'Choose "Display" as your campaign type to use image creatives.' },
      { step: 'Navigate to Assets', detail: 'Under "Ads & assets" → "Ads", click "New Ad" → "Responsive display ad".' },
      { step: 'Upload Images', detail: 'Click "Add images" and upload your exported assets. Google accepts multiple sizes; use ad-landscape.png (1200×628) and ad-square.png (1080×1080).' },
      { step: 'Add Headlines & Descriptions', detail: 'Enter up to 5 headlines and 5 descriptions from your ad-copy.txt. Google will test combinations automatically.' },
      { step: 'Preview & Save', detail: 'Preview your ad across placements, then click "Save" to add it to your Ad Group.' },
      { step: 'Set Bidding & Budget', detail: 'Set your daily budget and bidding strategy, then publish your campaign.' },
    ],
  },
];

interface UploadGuideDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function UploadGuideDrawer({ open, onClose }: UploadGuideDrawerProps) {
  const [active, setActive] = useState('meta');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [open]);

  if (!open && !visible) return null;

  const platform = PLATFORMS.find((p) => p.id === active) ?? PLATFORMS[0];

  return (
    <div
      className={`fixed inset-0 z-50 flex justify-end transition-all duration-300 ${visible && open ? 'bg-black/60' : 'bg-transparent pointer-events-none'}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className={`w-full max-w-lg bg-stone-950 border-l border-stone-800 h-full flex flex-col transition-transform duration-300 ${
        visible && open ? 'translate-x-0' : 'translate-x-full'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-800 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-500/20">
              <i className="ri-guide-line text-orange-400 text-base" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">Platform Upload Guide</h3>
              <p className="text-xs text-stone-500">Step-by-step publishing instructions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-500 hover:text-white hover:bg-stone-800 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-base" />
          </button>
        </div>

        {/* Platform tabs */}
        <div className="flex items-center gap-1 px-4 py-3 border-b border-stone-800 shrink-0">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                active === p.id
                  ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30'
                  : 'text-stone-400 hover:text-white hover:bg-stone-800'
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${p.icon} text-sm ${active === p.id ? 'text-orange-400' : p.color}`} />
              </div>
              {p.label}
            </button>
          ))}
        </div>

        {/* Steps */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-6 h-6 flex items-center justify-center">
              <i className={`${platform.icon} text-base ${platform.color}`} />
            </div>
            <h4 className="text-sm font-bold text-white">{platform.label}</h4>
          </div>

          <div className="flex flex-col gap-4">
            {platform.steps.map((s, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex flex-col items-center shrink-0">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-500/20 border border-orange-500/30 text-xs font-bold text-orange-400 shrink-0">
                    {i + 1}
                  </div>
                  {i < platform.steps.length - 1 && (
                    <div className="w-px flex-1 bg-stone-800 mt-2 min-h-4" />
                  )}
                </div>
                <div className="pb-4">
                  <div className="text-sm font-semibold text-white mb-1">{s.step}</div>
                  <p className="text-xs text-stone-500 leading-relaxed">{s.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-orange-500/10 border border-orange-500/20 rounded-xl px-4 py-3.5">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-lightbulb-line text-orange-400 text-sm" />
              </div>
              <span className="text-xs font-semibold text-orange-400">Pro Tip</span>
            </div>
            <p className="text-xs text-stone-400 leading-relaxed">
              Your export package includes a <span className="text-white font-medium">platform-instructions.txt</span> file with these same steps plus your ad copy pre-filled and ready to paste.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-stone-800 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-3 rounded-xl bg-stone-800 hover:bg-stone-700 text-stone-300 text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            Close Guide
          </button>
        </div>
      </div>
    </div>
  );
}
